import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Loader2, RefreshCw, Download, ExternalLink } from 'lucide-react';
import Reto21InsightsView from '../../components/Reto21InsightsView';
import authAxios, { API } from '../../utils/authAxios';
import { toast } from 'sonner';

export default function Reto21InsightsAdmin() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await authAxios.get(API + '/admin/reto21/insights');
      setInsights(res.data);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los insights del Reto 21');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const exportJSON = () => {
    if (!insights) return;
    const blob = new Blob([JSON.stringify(insights, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reto21-insights-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <Link to="/admin" className="text-emerald-400 hover:text-emerald-300 text-sm inline-flex items-center gap-1 mb-2" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" /> Volver al panel
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Reto 21 Días — Insights</h1>
            <p className="text-slate-400 mt-1">Análisis agregado de la cohorte de participantes.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              data-testid="refresh-insights-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportJSON}
              disabled={!insights}
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              data-testid="export-json-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar JSON
            </Button>
            <Link to="/estudio-reto-21-dias" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-emerald-600/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="view-public-btn">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver reporte público
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-40">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : (
          <>
            {insights?.summary?.total > 0 && insights.summary.total < 100 && (
              <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm" data-testid="threshold-warning">
                ℹ️ Reporte público disponible al alcanzar 100 participantes. Actualmente: {insights.summary.total}.
              </div>
            )}
            <Reto21InsightsView insights={insights} mode="admin" />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
